'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { useRole } from '@/contexts/RoleContext';
import { roleDashboardPaths, demoRoleMap } from '@/lib/roleConfig';

type LoginForm = {
  email: string;
  password: string;
  rememberMe: boolean;
};

type RegisterForm = {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  role: string;
  agreeTerms: boolean;
};

const demoCredentials = [
  { role: 'Super Admin', email: 'admin@srisevatrust.in', password: 'Admin@2024' },
  { role: 'Doctor', email: 'dr.meena@srisevatrust.in', password: 'Doctor@2024' },
  { role: 'Volunteer', email: 'ravi.volunteer@srisevatrust.in', password: 'Volunteer@2024' },
  { role: 'Member', email: 'priya.member@srisevatrust.in', password: 'Member@2024' },
  { role: 'Student', email: 'student@srisevatrust.in', password: 'Student@2024' },
  { role: 'Patient', email: 'patient@srisevatrust.in', password: 'Patient@2024' },
];

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'doctor', label: 'Doctor' },
  { value: 'volunteer', label: 'Volunteer' },
  { value: 'member', label: 'Member' },
  { value: 'student', label: 'Student' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'staff', label: 'Staff' },
];

const initiatives = [
  { icon: '🌿', text: 'Ayurveda & Holistic Healthcare' },
  { icon: '📚', text: 'Education & Skill Development' },
  { icon: '🤝', text: 'Social Welfare & Community Service' },
  { icon: '🧘', text: 'Yoga & Wellness Programs' },
  { icon: '⚖️', text: 'Human Rights Advocacy' },
];

export default function AuthScreen() {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { setRoleFromEmail } = useRole();

  const loginForm = useForm<LoginForm>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const registerForm = useForm<RegisterForm>({
    defaultValues: { fullName: '', email: '', phone: '', password: '', confirmPassword: '', role: '', agreeTerms: false },
  });

  const handleLogin = async (data: LoginForm) => {
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 1000));
    const valid = demoCredentials.find(c => c.email === data.email && c.password === data.password);
    if (!valid) {
      toast.error('Invalid credentials — use the demo accounts below to sign in');
      loginForm.setError('email', { message: ' ' });
      loginForm.setError('password', { message: 'Invalid email or password' });
      setIsLoading(false);
      return;
    }
    // Set role from email before redirecting
    setRoleFromEmail(data.email);
    const detectedRole = demoRoleMap[data.email] ?? 'admin';
    const dashPath = roleDashboardPaths[detectedRole];
    toast.success(`Welcome back, ${valid.role}!`);
    setTimeout(() => {
      window.location.href = dashPath;
    }, 600);
    setIsLoading(false);
  };

  const handleRegister = async (data: RegisterForm) => {
    if (data.password !== data.confirmPassword) {
      registerForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 1200));
    toast.success('Registration submitted! Await admin approval.');
    setIsLoading(false);
    setActiveTab('login');
  };

  const autofillCredentials = (cred: typeof demoCredentials[0]) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
    toast.info(`Autofilled ${cred.role} credentials`);
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col w-[480px] xl:w-[520px] 2xl:w-[560px] flex-shrink-0 gradient-saffron relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full"
            style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #ffffff 0%, transparent 50%), radial-gradient(circle at 80% 80%, #2D6A4F 0%, transparent 50%)' }}
          />
        </div>
        <div className="relative z-10 flex flex-col h-full p-10 xl:p-12">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
              <AppLogo size={36} />
            </div>
            <div>
              <p className="text-white font-bold text-lg leading-tight">SriMrityunjayan</p>
              <p className="text-white/70 text-sm">Seva Foundation</p>
            </div>
          </div>
          <div className="mb-10">
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
              Empowering Lives Through<br />
              <span className="text-accent">Ayurveda & Seva</span>
            </h1>
            <p className="text-white/80 text-base leading-relaxed">
              Founded in 2023, we are committed to improving lives through holistic healthcare, education, and social welfare across India.
            </p>
          </div>
          <div className="space-y-3 mb-10">
            {initiatives.map((item, i) => (
              <div key={`initiative-${i}`} className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-3">
                <span className="text-xl">{item.icon}</span>
                <span className="text-white/90 text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-4 mt-auto">
            {[
              { value: '2,400+', label: 'Members' },
              { value: '180+', label: 'Volunteers' },
              { value: '₹42L+', label: 'Donations' },
            ].map((stat, i) => (
              <div key={`stat-${i}`} className="text-center bg-white/10 rounded-xl px-3 py-4">
                <p className="text-white font-bold text-xl font-tabular">{stat.value}</p>
                <p className="text-white/70 text-xs mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-white/20">
            <p className="text-white/50 text-xs">CIN: U88900KA2023NPL178629 · RoC-Bangalore · Est. 2023</p>
            <p className="text-white/50 text-xs mt-1">21km off Kanakapura Road, Udayapura, Bangalore-560082</p>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10 overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <AppLogo size={40} />
            <div>
              <p className="font-bold text-lg text-foreground leading-tight">SriMrityunjayan Seva Foundation</p>
              <p className="text-sm text-muted-foreground">Admin Platform</p>
            </div>
          </div>

          <div className="flex bg-muted rounded-xl p-1 mb-8">
            {(['login', 'register'] as const).map(tab => (
              <button
                key={`tab-${tab}`}
                onClick={() => setActiveTab(tab)}
                className={[
                  'flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200',
                  activeTab === tab ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground',
                ].join(' ')}
              >
                {tab === 'login' ? 'Sign In' : 'Register'}
              </button>
            ))}
          </div>

          {activeTab === 'login' && (
            <div className="slide-up">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground">Welcome back</h2>
                <p className="text-muted-foreground text-sm mt-1">Sign in to your foundation account</p>
              </div>
              <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-5">
                <button type="button" className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border border-border bg-card text-sm font-medium text-foreground hover:bg-muted transition-all duration-150 active:scale-95">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">or sign in with email</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <div>
                  <label className="label-text" htmlFor="login-email">Email Address</label>
                  <input
                    id="login-email"
                    type="email"
                    placeholder="admin@srisevatrust.in"
                    className={`input-field ${loginForm.formState.errors.email ? 'input-error' : ''}`}
                    {...loginForm.register('email', {
                      required: 'Email is required',
                      pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email' },
                    })}
                  />
                  {loginForm.formState.errors.email?.message && loginForm.formState.errors.email.message.trim() && (
                    <p className="error-text">{loginForm.formState.errors.email.message}</p>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="label-text" htmlFor="login-password">Password</label>
                    <button type="button" className="text-xs text-primary hover:underline">Forgot password?</button>
                  </div>
                  <div className="relative">
                    <input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      className={`input-field pr-10 ${loginForm.formState.errors.password ? 'input-error' : ''}`}
                      {...loginForm.register('password', {
                        required: 'Password is required',
                        minLength: { value: 6, message: 'Minimum 6 characters' },
                      })}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="error-text">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input id="remember-me" type="checkbox" className="w-4 h-4 rounded border-border accent-primary" {...loginForm.register('rememberMe')} />
                  <label htmlFor="remember-me" className="text-sm text-muted-foreground">Remember me for 30 days</label>
                </div>
                <button type="submit" disabled={isLoading} className="btn-primary w-full py-3 text-base">
                  {isLoading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Signing in...
                    </>
                  ) : 'Sign In to Dashboard'}
                </button>
              </form>

              {/* Demo credentials */}
              <div className="mt-6 rounded-xl border border-border bg-muted/50 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-border bg-muted">
                  <p className="text-xs font-semibold text-foreground">Demo Credentials — Click to autofill</p>
                </div>
                <div className="divide-y divide-border">
                  {demoCredentials.map((cred, i) => (
                    <div key={`cred-${i}`} className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/80 transition-colors">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-foreground">{cred.role}</p>
                        <p className="text-xs text-muted-foreground truncate">{cred.email}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => autofillCredentials(cred)}
                        className="flex-shrink-0 ml-3 px-2.5 py-1 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors active:scale-95"
                      >
                        Use
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'register' && (
            <div className="slide-up">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground">Create account</h2>
                <p className="text-muted-foreground text-sm mt-1">Join the SriMrityunjayan Seva Foundation</p>
              </div>
              <form onSubmit={registerForm.handleSubmit(handleRegister)} className="space-y-4">
                <div>
                  <label className="label-text" htmlFor="reg-name">Full Name</label>
                  <input id="reg-name" type="text" placeholder="Priya Sharma" className={`input-field ${registerForm.formState.errors.fullName ? 'input-error' : ''}`}
                    {...registerForm.register('fullName', { required: 'Full name is required', minLength: { value: 2, message: 'Name too short' } })} />
                  {registerForm.formState.errors.fullName && <p className="error-text">{registerForm.formState.errors.fullName.message}</p>}
                </div>
                <div>
                  <label className="label-text" htmlFor="reg-email">Email Address</label>
                  <input id="reg-email" type="email" placeholder="priya@example.com" className={`input-field ${registerForm.formState.errors.email ? 'input-error' : ''}`}
                    {...registerForm.register('email', { required: 'Email is required', pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email' } })} />
                  {registerForm.formState.errors.email && <p className="error-text">{registerForm.formState.errors.email.message}</p>}
                </div>
                <div>
                  <label className="label-text" htmlFor="reg-phone">Phone Number</label>
                  <div className="flex gap-2">
                    <div className="flex items-center px-3 py-2.5 rounded-lg border border-input bg-muted text-sm text-muted-foreground flex-shrink-0">+91</div>
                    <input id="reg-phone" type="tel" placeholder="98765 43210" className={`input-field flex-1 ${registerForm.formState.errors.phone ? 'input-error' : ''}`}
                      {...registerForm.register('phone', { required: 'Phone is required', pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit Indian mobile number' } })} />
                  </div>
                  {registerForm.formState.errors.phone && <p className="error-text">{registerForm.formState.errors.phone.message}</p>}
                </div>
                <div>
                  <label className="label-text" htmlFor="reg-role">Register As</label>
                  <select id="reg-role" className={`input-field ${registerForm.formState.errors.role ? 'input-error' : ''}`}
                    {...registerForm.register('role', { required: 'Please select a role' })}>
                    <option value="">Select your role</option>
                    {roleOptions.map(r => <option key={`role-${r.value}`} value={r.value}>{r.label}</option>)}
                  </select>
                  <p className="helper-text">Your account will be reviewed by an admin before activation</p>
                  {registerForm.formState.errors.role && <p className="error-text">{registerForm.formState.errors.role.message}</p>}
                </div>
                <div>
                  <label className="label-text" htmlFor="reg-password">Password</label>
                  <div className="relative">
                    <input id="reg-password" type={showPassword ? 'text' : 'password'} placeholder="Min. 8 characters"
                      className={`input-field pr-10 ${registerForm.formState.errors.password ? 'input-error' : ''}`}
                      {...registerForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Minimum 8 characters' }, pattern: { value: /^(?=.*[A-Z])(?=.*\d)/, message: 'Must include 1 uppercase and 1 number' } })} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    </button>
                  </div>
                  {registerForm.formState.errors.password && <p className="error-text">{registerForm.formState.errors.password.message}</p>}
                </div>
                <div>
                  <label className="label-text" htmlFor="reg-confirm">Confirm Password</label>
                  <div className="relative">
                    <input id="reg-confirm" type={showConfirmPassword ? 'text' : 'password'} placeholder="Re-enter your password"
                      className={`input-field pr-10 ${registerForm.formState.errors.confirmPassword ? 'input-error' : ''}`}
                      {...registerForm.register('confirmPassword', { required: 'Please confirm your password' })} />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    </button>
                  </div>
                  {registerForm.formState.errors.confirmPassword && <p className="error-text">{registerForm.formState.errors.confirmPassword.message}</p>}
                </div>
                <div className="flex items-start gap-2">
                  <input id="agree-terms" type="checkbox" className="w-4 h-4 mt-0.5 rounded border-border accent-primary"
                    {...registerForm.register('agreeTerms', { required: 'You must agree to the terms' })} />
                  <label htmlFor="agree-terms" className="text-sm text-muted-foreground">
                    I agree to the <Link href="/terms" className="text-primary hover:underline">Terms of Service</Link> and <Link href="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
                  </label>
                </div>
                {registerForm.formState.errors.agreeTerms && <p className="error-text">{registerForm.formState.errors.agreeTerms.message}</p>}
                <button type="submit" disabled={isLoading} className="btn-primary w-full py-3 text-base">
                  {isLoading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Submitting...
                    </>
                  ) : 'Create Account'}
                </button>
              </form>
            </div>
          )}

          <p className="text-center text-xs text-muted-foreground mt-6">
            SriMrityunjayan Seva Foundation · CIN: U88900KA2023NPL178629
          </p>
        </div>
      </div>
    </div>
  );
}